import React, { useState } from 'react';
import { CheckCircle, XCircle, ChevronDown, ChevronUp, AlertTriangle, Clock, Monitor, Calendar, Zap, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const precos = [
  {
    titulo: 'Tela 14"',
    subtitulo: 'HD ou FHD — modelos comuns',
    peca: 'R$ 399 – R$ 499',
    mo: 'R$ 300',
    total: 'R$ 699 – R$ 799',
    inclui: ['Tela 14" compatível ou original', 'Mão de obra R$ 300', 'Teste completo de imagem', 'Garantia de 90 dias'],
    destaque: false,
    icon: <Monitor className="w-5 h-5" />,
  },
  {
    titulo: 'Tela 15.6" FHD',
    subtitulo: 'IPS Full HD — mais comum',
    peca: 'R$ 450 – R$ 650',
    mo: 'R$ 300',
    total: 'R$ 750 – R$ 950',
    inclui: ['Tela 15.6" 1920×1080 IPS', 'Mão de obra R$ 300', 'Teste de cor e brilho', 'Garantia de 90 dias'],
    destaque: true,
    icon: <Zap className="w-5 h-5" />,
  },
  {
    titulo: 'Tela Touch / 2-in-1',
    subtitulo: 'Com digitalizador touchscreen',
    peca: 'R$ 700 – R$ 950',
    mo: 'R$ 300',
    total: 'R$ 1.000 – R$ 1.250',
    inclui: ['Tela touchscreen original', 'Digitalizador e vidro integrado', 'Mão de obra R$ 300', 'Calibração do touch', 'Garantia de 90 dias'],
    destaque: false,
    icon: <Shield className="w-5 h-5" />,
  },
];

const causasComuns = [
  { ok: true,  texto: 'Tela quebrada, rachada ou com manchas físicas' },
  { ok: true,  texto: 'Linhas verticais ou horizontais permanentes na tela' },
  { ok: true,  texto: 'Metade da tela escura ou sem imagem' },
  { ok: true,  texto: 'Retroiluminação falhou (tela escura, imagem visível com lanterna)' },
  { ok: true,  texto: 'Pixels mortos em área central ou em grande quantidade' },
];

const naoPrecisaTrocar = [
  { ok: false, texto: 'Problema de driver de vídeo (imagem distorcida mas funciona no externo)' },
  { ok: false, texto: 'Cabo flat da tela com mau contato (R$ 120–R$ 220 para substituir o cabo)' },
  { ok: false, texto: 'Brilho zerado acidentalmente (verificar Fn + tecla de brilho)' },
];

const faqs = [
  {
    pergunta: 'Quanto custa trocar a tela de um notebook em Ribeirão Preto?',
    resposta: 'Em Ribeirão Preto, a peça (tela) custa entre R$ 399 e R$ 650 dependendo do modelo. A mão de obra de substituição é R$ 300. O total fica entre R$ 699 e R$ 950 para notebooks comuns (14" e 15.6" FHD). Telas touchscreen têm custo mais elevado. Na Fernando Tech o diagnóstico é gratuito.',
  },
  {
    pergunta: 'Vale a pena trocar a tela do notebook ou comprar um novo?',
    resposta: 'Se o notebook tem menos de 5 anos e bom processador, trocar a tela quase sempre compensa. Um notebook novo de entrada custa R$ 1.800 ou mais, enquanto a troca fica entre R$ 699 e R$ 950 na maioria dos casos — além de preservar seus dados e configurações.',
  },
  {
    pergunta: 'Quanto tempo leva para trocar a tela do notebook?',
    resposta: 'A troca leva de 1 a 3 horas para notebooks comuns. Ultrabooks e modelos com tela colada podem levar de 3 a 5 horas. Na Fernando Tech entregamos no mesmo dia na maioria dos casos.',
  },
  {
    pergunta: 'Como saber se preciso trocar a tela ou só o cabo flat?',
    resposta: 'Conecte um monitor externo no notebook. Se a imagem aparecer normalmente no externo, o problema pode ser apenas no cabo flat (R$ 120–R$ 220) e não na tela. Nosso técnico identifica a causa exata no diagnóstico gratuito.',
  },
  {
    pergunta: 'A troca de tela tem garantia?',
    resposta: 'Sim. Todos os serviços de troca de tela na Fernando Tech têm garantia de 90 dias na peça e na mão de obra. Usamos telas compatíveis testadas ou originais (a escolha do cliente).',
  },
  {
    pergunta: 'Vocês trocam tela de qualquer marca de notebook?',
    resposta: 'Sim. Atendemos Dell, HP, Lenovo, Samsung, Acer, Asus, Positivo e outras marcas. A disponibilidade da peça pode variar por modelo — confirmamos no orçamento gratuito.',
  },
];

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-700/60 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-slate-800/60 hover:bg-slate-700/60 transition-colors"
        aria-expanded={open}
      >
        <span className="text-slate-100 font-medium text-sm leading-snug pr-4">{item.pergunta}</span>
        {open ? <ChevronUp className="w-4 h-4 text-cyan-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-5 py-4 bg-slate-900/60 text-slate-300 text-sm leading-relaxed border-t border-slate-700/40">
          {item.resposta}
        </div>
      )}
    </div>
  );
}

export default function ArtigoTrocaTelaNotes({ onAgendar }) {
  return (
    <article className="max-w-4xl mx-auto px-4 py-12" itemScope itemType="https://schema.org/Article">

      {/* Manchete */}
      <header className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-violet-400 bg-violet-400/10 px-3 py-1 rounded-full">
            Guia 2026
          </span>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Leitura: ~4 min
          </span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight mb-4" itemProp="headline">
          Quanto Custa Trocar a Tela de Notebook em 2026?
        </h2>
        <p className="text-slate-400 text-sm">
          Por <span className="text-violet-400 font-medium">Fernando Tech</span> · Ribeirão Preto, SP ·
          Atualizado em janeiro de 2026
        </p>
      </header>

      {/* Resposta Direta */}
      <section className="bg-gradient-to-r from-violet-950/60 to-slate-800/60 border border-violet-700/40 rounded-2xl p-6 mb-10" aria-label="Resposta rápida">
        <p className="text-xs font-bold uppercase tracking-widest text-violet-400 mb-2">Resposta rápida</p>
        <p className="text-white text-lg font-semibold leading-relaxed">
          Em 2026, a <strong>peça (tela)</strong> custa entre{' '}
          <span className="text-violet-300 font-bold">R$ 399 e R$ 650</span> dependendo do modelo.
          A <strong>mão de obra</strong> de substituição é{' '}
          <span className="text-violet-300 font-bold">R$ 300</span>, fixo.
          O total para notebooks comuns fica entre{' '}
          <span className="text-violet-300 font-bold">R$ 699 e R$ 950</span>.
          Telas touchscreen têm custo mais elevado. Em Ribeirão Preto, a{' '}
          <strong>Fernando Tech</strong> faz diagnóstico gratuito e entrega no mesmo dia na maioria dos casos.
        </p>
      </section>

      {/* O que causa danos na tela */}
      <section className="mb-10">
        <h3 className="text-xl font-bold text-white mb-3">O que causa dano na tela do notebook?</h3>
        <p className="text-slate-400 leading-relaxed mb-4">
          A tela é a peça mais frágil do notebook. Os danos mais comuns são queda com o equipamento fechado,
          pressão excessiva sobre a tampa, líquido derramado e problemas elétricos no painel ou no cabo flat.
          Nem todo defeito visual exige troca da tela — o diagnóstico técnico identifica a causa exata.
        </p>
      </section>

      {/* Tabela de Preços */}
      <section className="mb-12">
        <h3 className="text-xl font-bold text-white mb-6">Tabela de preços — troca de tela de notebook em 2026</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {precos.map(p => (
            <div
              key={p.titulo}
              className={`relative rounded-2xl border p-5 transition-all ${
                p.destaque
                  ? 'border-violet-500/60 bg-gradient-to-br from-violet-950/80 to-slate-800/80 shadow-lg shadow-violet-500/10'
                  : 'border-slate-700/50 bg-slate-800/50 hover:border-slate-600/60'
              }`}
            >
              {p.destaque && (
                <span className="absolute -top-3 left-4 bg-violet-500 text-slate-950 text-[11px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wide">
                  Mais comum
                </span>
              )}
              <div className="flex items-center gap-2 mb-1">
                <span className={p.destaque ? 'text-violet-400' : 'text-slate-400'}>{p.icon}</span>
                <h4 className="text-white font-bold text-base">{p.titulo}</h4>
              </div>
              <p className="text-slate-500 text-xs mb-4">{p.subtitulo}</p>

              {/* Breakdown peça + MO + total */}
              <div className="space-y-1.5 mb-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">Peça (tela)</span>
                  <span className="text-slate-200 font-semibold">{p.peca}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-400">Mão de obra</span>
                  <span className="text-slate-200 font-semibold">{p.mo}</span>
                </div>
                <div className="h-px bg-slate-700/50 my-1" />
                <div className="flex justify-between items-center">
                  <span className="text-slate-300 font-semibold text-sm">Total estimado</span>
                  <span className={`font-extrabold text-lg ${p.destaque ? 'text-violet-300' : 'text-slate-100'}`}>{p.total}</span>
                </div>
              </div>

              <ul className="space-y-1.5 border-t border-slate-700/40 pt-3">
                {p.inclui.map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-400">
                    <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-slate-500 text-xs mt-4 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
          Preços de referência para Ribeirão Preto. Confirmados no diagnóstico gratuito conforme modelo.
        </p>
      </section>

      {/* Quando trocar / quando não precisa */}
      <section className="mb-10">
        <h3 className="text-xl font-bold text-white mb-5">Quando a tela precisa ser trocada?</h3>
        <div className="grid sm:grid-cols-2 gap-6 mb-4">
          <div>
            <p className="text-emerald-400 font-semibold text-sm mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Exige troca da tela
            </p>
            <ul className="space-y-2">
              {causasComuns.map(s => (
                <li key={s.texto} className="flex items-start gap-2 text-sm text-slate-400">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {s.texto}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-amber-400 font-semibold text-sm mb-3 flex items-center gap-2">
              <XCircle className="w-4 h-4" /> Pode não precisar de troca
            </p>
            <ul className="space-y-2">
              {naoPrecisaTrocar.map(s => (
                <li key={s.texto} className="flex items-start gap-2 text-sm text-slate-400">
                  <XCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" /> {s.texto}
                </li>
              ))}
            </ul>
            <p className="text-slate-500 text-xs mt-4 leading-relaxed">
              Por isso o diagnóstico gratuito é fundamental — evita gasto desnecessário com peça.
            </p>
          </div>
        </div>
      </section>

      {/* Trocar ou comprar notebook novo */}
      <section className="mb-12">
        <h3 className="text-xl font-bold text-white mb-4">Vale a pena trocar ou comprar um notebook novo?</h3>
        <div className="bg-slate-800/60 border border-slate-700/40 rounded-xl p-5">
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3 text-slate-300">
              <span className="bg-violet-500/20 text-violet-300 font-bold rounded px-2 py-0.5 text-xs shrink-0 mt-0.5">Notebook &lt; 4 anos</span>
              Troque a tela. Custo total de R$ 699–R$ 950 x notebook novo de entrada (R$ 1.800+): a troca é até 2× mais barata.
            </li>
            <li className="flex items-start gap-3 text-slate-300">
              <span className="bg-amber-500/20 text-amber-300 font-bold rounded px-2 py-0.5 text-xs shrink-0 mt-0.5">Notebook 4–7 anos</span>
              Avalie o custo-benefício. Se hardware ainda atende, vale trocar. Podemos recomendar upgrade de RAM/SSD junto.
            </li>
            <li className="flex items-start gap-3 text-slate-300">
              <span className="bg-rose-500/20 text-rose-300 font-bold rounded px-2 py-0.5 text-xs shrink-0 mt-0.5">Notebook &gt; 7 anos</span>
              Analise antes. Se a bateria, teclado e hardware já apresentam outros desgastes, pode valer renovar o equipamento.
            </li>
          </ul>
        </div>
      </section>

      {/* CTA Local */}
      <section className="bg-gradient-to-br from-slate-800/80 to-violet-950/60 border border-violet-700/30 rounded-2xl p-7 mb-12 text-center">
        <h3 className="text-xl font-bold text-white mb-2">Troca de Tela de Notebook em Ribeirão Preto</h3>
        <p className="text-slate-400 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
          A <strong className="text-white">Fernando Tech</strong> troca telas de todas as marcas em Ribeirão Preto.
          Diagnóstico gratuito, entrega em até 24h, garantia de 90 dias. Atendemos Dell, HP, Lenovo, Acer, Asus,
          Samsung, Positivo, Apple e outras marcas.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={onAgendar}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 text-white font-bold rounded-full shadow-lg shadow-violet-500/20 transition-all text-sm"
            data-testid="artigo-tela-agendar-btn"
          >
            <Calendar className="w-4 h-4" /> Agendar Diagnóstico Gratuito
          </button>
          <a
            href="https://wa.me/5516991378312?text=Olá!%20Preciso%20trocar%20a%20tela%20do%20meu%20notebook."
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full shadow-lg shadow-emerald-500/20 transition-all text-sm"
            data-testid="artigo-tela-whatsapp-btn"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L0 24l6.338-1.497A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.789 9.789 0 01-4.99-1.364l-.358-.213-3.76.888.937-3.647-.234-.374A9.786 9.786 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/></svg>
            Pedir Orçamento pelo WhatsApp
          </a>
        </div>
        <p className="text-slate-500 text-xs mt-4">Atendemos Ribeirão Preto, Sertãozinho, Jardinópolis e região.</p>
      </section>

      {/* FAQ */}
      <section aria-label="Perguntas frequentes sobre troca de tela de notebook">
        <h3 className="text-xl font-bold text-white mb-5">Perguntas frequentes — troca de tela de notebook</h3>
        <div className="space-y-2">
          {faqs.map(item => <FaqItem key={item.pergunta} item={item} />)}
        </div>
      </section>

      {/* Links internos */}
      <section className="mt-10 pt-8 border-t border-slate-800">
        <p className="text-slate-500 text-xs uppercase tracking-widest mb-4">Veja também</p>
        <div className="flex flex-wrap gap-3">
          {[
            { label: 'Quanto custa formatar um computador?', href: '/assistencia-tecnica#artigo-formatacao' },
            { label: 'Assistência Daten Autorizada', href: '/daten' },
            { label: 'Orçamento Câmeras Intelbras', href: '/orcamento-intelbras' },
          ].map(link => (
            <Link key={link.href} to={link.href} className="text-sm text-violet-400 hover:text-violet-300 border border-violet-800/50 hover:border-violet-600/60 px-4 py-1.5 rounded-full transition-all">
              {link.label}
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}
